import { RuntimeDefectReporter } from '@st/redux'
import { Cause, Context, Duration, Effect, Exit, Layer, PubSub, Scope, Stream } from 'effect'
import type { Service } from 'effect/Context'
import {
  type DocumentRendererEvent,
  DocumentRendererOpenError,
  type DocumentRendererService,
  type DocumentRendererSessionOpened
} from '../document-viewer-services'
import { PageTextAssets } from '../document-viewer-presented-page'
import { type PageStructure, type ViewerPage } from '../models'
import { makeMuPDFRenderingEngine, type MuPDFRenderingEngine } from './mupdf/mupdf-rendering-engine'
import { BrowserMuPDFPlatformLive } from './mupdf/mupdf-browser-platform'
import type { MuPDFWorkerCrashedError } from './mupdf/mupdf-platform'
import { BrowserPageSurfaceLive, PageSurface } from './page-surface'
import type { PDFSourceInput } from './pdf-source-input'

type PDFPageRuntime = {
  scope: Scope.Closeable
  bitmap: ImageBitmap | null
  pageTextId: string | null
}

type PDFRendererSession = {
  scope: Scope.Closeable
  engine: MuPDFRenderingEngine
  enableStructuredText: boolean
  pageRuntimeByPageNumber: Map<number, PDFPageRuntime>
  surfaceIdByPageNumber: Map<number, string>
  events: PubSub.PubSub<DocumentRendererEvent>
}

type StoredAssetLocation = {
  sessionId: string
  pageNumber: number
}

const OPEN_DOCUMENT_TIMEOUT_MS = 20_000

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }
  return new Error(String(error))
}

export class PDFDocumentRenderer extends Context.Service<
  PDFDocumentRenderer,
  DocumentRendererService<unknown, ViewerPage>
>()('PDFDocumentRenderer') {}

export function createPDFDocumentRendererLayer(
  rendererTag: Service<any, DocumentRendererService<unknown, ViewerPage>>
) {
  return Layer.effect(
    rendererTag,
    Effect.gen(function* () {
      const sessions = new Map<string, PDFRendererSession>()
      const pageTextLocations = new Map<string, StoredAssetLocation>()
      let nextRuntimeId = 1
      const createRuntimeId = (prefix: string) => {
        const id = `${prefix}:${nextRuntimeId}`
        nextRuntimeId += 1
        return id
      }
      const defectReporter = yield* RuntimeDefectReporter
      const pageSurface = yield* PageSurface
      const pageTextAssets = yield* PageTextAssets
      const openRenderingEngine = yield* makeMuPDFRenderingEngine
      const reportDetachedCause = (context: string, cause: Cause.Cause<unknown>) => {
        if (Cause.hasInterruptsOnly(cause)) {
          return Effect.void
        }
        return defectReporter.report(context, Cause.squash(cause))
      }

      const createPageRuntime = () =>
        Effect.gen(function* () {
          const scope = yield* Scope.make()
          return {
            scope,
            bitmap: null,
            pageTextId: null
          } satisfies PDFPageRuntime
        })

      const resolvePageContext = ({
        sessionId,
        pageNumber,
        expectedRuntime
      }: {
        sessionId: string
        pageNumber: number
        expectedRuntime?: PDFPageRuntime
      }) => {
        const session = sessions.get(sessionId)
        if (!session) {
          return null
        }

        const pageRuntime = session.pageRuntimeByPageNumber.get(pageNumber)
        if (!pageRuntime) {
          return null
        }

        if (expectedRuntime && pageRuntime != expectedRuntime) {
          return null
        }

        return {
          session,
          pageRuntime
        }
      }

      const releaseAsset = <StoredAsset extends StoredAssetLocation>({
        assetId,
        assets,
        getCurrentAssetId,
        clearCurrentAssetId,
        cleanup
      }: {
        assetId: string
        assets: Map<string, StoredAsset>
        getCurrentAssetId: (pageRuntime: PDFPageRuntime) => string | null
        clearCurrentAssetId: (pageRuntime: PDFPageRuntime) => void
        cleanup?: (storedAsset: StoredAsset) => void
      }) => {
        const storedAsset = assets.get(assetId)
        if (!storedAsset) {
          return
        }

        assets.delete(assetId)

        const pageContext = resolvePageContext({
          sessionId: storedAsset.sessionId,
          pageNumber: storedAsset.pageNumber
        })

        if (pageContext && getCurrentAssetId(pageContext.pageRuntime) == assetId) {
          clearCurrentAssetId(pageContext.pageRuntime)
        }

        cleanup?.(storedAsset)
      }

      const discardBitmapInternal = (pageRuntime: PDFPageRuntime) => {
        if (!pageRuntime.bitmap) {
          return
        }

        pageRuntime.bitmap.close()
        pageRuntime.bitmap = null
      }

      const discardPageTextInternal = (pageTextId: string) =>
        Effect.sync(() => {
          releaseAsset({
            assetId: pageTextId,
            assets: pageTextLocations,
            getCurrentAssetId: (pageRuntime) => pageRuntime.pageTextId,
            clearCurrentAssetId: (pageRuntime) => {
              pageRuntime.pageTextId = null
            }
          })
          pageTextAssets.remove(pageTextId)
        })

      const closePageRuntime = ({
        sessionId,
        pageNumber
      }: {
        sessionId: string
        pageNumber: number
      }) =>
        Effect.gen(function* () {
          const session = sessions.get(sessionId)
          if (!session) {
            return
          }

          const pageRuntime = session.pageRuntimeByPageNumber.get(pageNumber)
          if (!pageRuntime) {
            return
          }

          session.pageRuntimeByPageNumber.delete(pageNumber)

          yield* Scope.close(pageRuntime.scope, Exit.succeed(undefined))

          discardBitmapInternal(pageRuntime)

          if (pageRuntime.pageTextId) {
            yield* discardPageTextInternal(pageRuntime.pageTextId)
          }
        })

      const closeSessionInternal = (sessionId: string) =>
        Effect.gen(function* () {
          const session = sessions.get(sessionId)
          if (!session) {
            return
          }

          for (const pageNumber of [...session.pageRuntimeByPageNumber.keys()]) {
            yield* closePageRuntime({
              sessionId,
              pageNumber
            })
          }

          sessions.delete(sessionId)
          yield* PubSub.shutdown(session.events)
          yield* Scope.close(session.scope, Exit.succeed(undefined))
        })

      const publishPageRenderFailed = ({
        error,
        sessionId,
        pageNumber,
        pageRuntime
      }: {
        error: Error | MuPDFWorkerCrashedError
        sessionId: string
        pageNumber: number
        pageRuntime: PDFPageRuntime
      }) =>
        Effect.gen(function* () {
          const pageContext = resolvePageContext({
            sessionId,
            pageNumber,
            expectedRuntime: pageRuntime
          })
          if (!pageContext) {
            return
          }

          yield* PubSub.publish(pageContext.session.events, {
            type: 'pageRenderFailed',
            sessionId,
            pageNumber,
            error: toError(error).message
          })
        })

      const renderBitmapForPage = ({
        sessionId,
        pageNumber,
        pageRuntime,
        session
      }: {
        sessionId: string
        pageNumber: number
        pageRuntime: PDFPageRuntime
        session: PDFRendererSession
      }) =>
        Effect.gen(function* () {
          const bitmap = yield* session.engine.renderPage(pageNumber)

          const pageContext = resolvePageContext({
            sessionId,
            pageNumber,
            expectedRuntime: pageRuntime
          })
          if (!pageContext) {
            bitmap.close()
            return
          }

          discardBitmapInternal(pageContext.pageRuntime)

          pageContext.pageRuntime.bitmap = bitmap

          const surfaceId = pageContext.session.surfaceIdByPageNumber.get(pageNumber)
          if (surfaceId) {
            yield* pageSurface.present(surfaceId, bitmap)
          }

          yield* PubSub.publish(pageContext.session.events, {
            type: 'pageRendered',
            render: {
              sessionId,
              pageNumber
            }
          })
        }).pipe(
          Effect.catchTags({
            MuPDFPageRenderError: (error) =>
              publishPageRenderFailed({ error, sessionId, pageNumber, pageRuntime }),
            MuPDFWorkerCrashedError: (error) =>
              publishPageRenderFailed({ error, sessionId, pageNumber, pageRuntime })
          }),
          Effect.asVoid
        )

      const extractTextForPage = ({
        sessionId,
        pageNumber,
        pageRuntime,
        session
      }: {
        sessionId: string
        pageNumber: number
        pageRuntime: PDFPageRuntime
        session: PDFRendererSession
      }) =>
        Effect.gen(function* () {
          const pageText = yield* session.engine.getPageStructuredText(pageNumber)

          const pageContext = resolvePageContext({
            sessionId,
            pageNumber,
            expectedRuntime: pageRuntime
          })
          if (!pageContext) {
            return
          }

          if (pageContext.pageRuntime.pageTextId) {
            yield* discardPageTextInternal(pageContext.pageRuntime.pageTextId)
          }

          const pageTextId = createRuntimeId('page-text')
          pageTextLocations.set(pageTextId, {
            sessionId,
            pageNumber
          })
          pageContext.pageRuntime.pageTextId = pageTextId
          pageTextAssets.store(pageTextId, pageText)

          yield* PubSub.publish(pageContext.session.events, {
            type: 'pageTextExtracted',
            sessionId,
            pageNumber,
            pageTextId
          })
        }).pipe(
          // Structured text is optional. A page-specific extraction failure does
          // not invalidate the rendered page; worker crashes are reported once by
          // the worker boundary and likewise need no second report here.
          Effect.catchTags({
            MuPDFPageTextExtractionError: () => Effect.void,
            MuPDFWorkerCrashedError: () => Effect.void
          })
        )

      const startPageLifecycle = ({
        sessionId,
        pageNumber,
        pageRuntime,
        session
      }: {
        sessionId: string
        pageNumber: number
        pageRuntime: PDFPageRuntime
        session: PDFRendererSession
      }) =>
        Effect.gen(function* () {
          yield* renderBitmapForPage({
            sessionId,
            pageNumber,
            pageRuntime,
            session
          })

          if (!session.enableStructuredText) {
            return
          }

          yield* Effect.sleep(Duration.seconds(2))

          yield* extractTextForPage({
            sessionId,
            pageNumber,
            pageRuntime,
            session
          })
        }).pipe(
          Effect.tapCause((cause) =>
            reportDetachedCause(
              `document renderer page lifecycle sessionId=${sessionId} pageNumber=${pageNumber}`,
              cause
            )
          ),
          Effect.forkIn(pageRuntime.scope)
        )

      const syncDesiredPages = ({
        sessionId,
        desiredPageNumbers
      }: {
        sessionId: string
        desiredPageNumbers: Set<number>
      }) =>
        Effect.gen(function* () {
          const session = sessions.get(sessionId)
          if (!session) {
            return
          }

          for (const pageNumber of [...session.pageRuntimeByPageNumber.keys()]) {
            if (desiredPageNumbers.has(pageNumber)) {
              continue
            }

            yield* closePageRuntime({
              sessionId,
              pageNumber
            })
          }

          for (const pageNumber of desiredPageNumbers) {
            if (session.pageRuntimeByPageNumber.has(pageNumber)) {
              continue
            }

            const pageRuntime = yield* createPageRuntime()
            session.pageRuntimeByPageNumber.set(pageNumber, pageRuntime)

            yield* startPageLifecycle({
              sessionId,
              pageNumber,
              pageRuntime,
              session
            })
          }
        })

      yield* Effect.addFinalizer(() =>
        Effect.gen(function* () {
          for (const sessionId of [...sessions.keys()]) {
            yield* closeSessionInternal(sessionId)
          }
        })
      )

      const service: DocumentRendererService<PDFSourceInput, PageStructure> = {
        open: (input) =>
          Effect.gen(function* () {
            const sessionId = createRuntimeId('doc-session')
            const sessionScope = yield* Scope.make()
            const sessionExit = yield* Effect.gen(function* () {
              const engine = yield* Scope.provide(
                openRenderingEngine({
                  documentUrl: input.src,
                  devicePixelRatio: input.devicePixelRatio ?? 1
                }),
                sessionScope
              )

              const events = yield* PubSub.unbounded<DocumentRendererEvent>()

              return {
                scope: sessionScope,
                engine,
                enableStructuredText: input.enableStructuredText == true,
                pageRuntimeByPageNumber: new Map(),
                surfaceIdByPageNumber: new Map(),
                events
              } satisfies PDFRendererSession
            }).pipe(
              Effect.timeoutOrElse({
                duration: Duration.millis(OPEN_DOCUMENT_TIMEOUT_MS),
                orElse: () =>
                  Effect.fail(
                    new Error(
                      `Document renderer open timed out after ${OPEN_DOCUMENT_TIMEOUT_MS}ms`
                    )
                  )
              }),
              Effect.exit
            )

            if (Exit.isFailure(sessionExit)) {
              yield* Scope.close(sessionScope, sessionExit)
              return yield* Effect.failCause(sessionExit.cause)
            }

            const session = sessionExit.value
            sessions.set(sessionId, session)

            return {
              sessionId,
              pages: session.engine.documentStructure.pages,
              renderMode: 'bitmap'
            } satisfies DocumentRendererSessionOpened<PageStructure>
          }).pipe(Effect.mapError((error) => new DocumentRendererOpenError(error))),

        close: (sessionId) => closeSessionInternal(sessionId),

        setDesiredPages: (sessionId, pages) =>
          Effect.gen(function* () {
            const session = sessions.get(sessionId)
            if (!session) {
              return
            }

            const totalPageCount = session.engine.documentStructure.pages.length
            const desiredPageNumbers = new Set(
              [...new Set(pages)]
                .sort((a, b) => a - b)
                .filter((pageNumber) => {
                  return pageNumber >= 1 && pageNumber <= totalPageCount
                })
            )

            yield* syncDesiredPages({
              sessionId,
              desiredPageNumbers
            })
          }),

        events: (sessionId) => {
          const session = sessions.get(sessionId)
          if (!session) {
            return Effect.succeed(Stream.empty)
          }

          return Effect.succeed(Stream.fromPubSub(session.events))
        },

        attachPageSurface: (sessionId, pageNumber, surfaceId) =>
          Effect.suspend(() => {
            const session = sessions.get(sessionId)
            if (!session) {
              return Effect.void
            }

            if (session.surfaceIdByPageNumber.get(pageNumber) == surfaceId) {
              return Effect.void
            }

            session.surfaceIdByPageNumber.set(pageNumber, surfaceId)
            const bitmap = session.pageRuntimeByPageNumber.get(pageNumber)?.bitmap
            if (!bitmap) {
              return Effect.void
            }

            return pageSurface.present(surfaceId, bitmap)
          }),

        detachPageSurface: (sessionId, pageNumber, surfaceId) =>
          Effect.sync(() => {
            const session = sessions.get(sessionId)
            if (session?.surfaceIdByPageNumber.get(pageNumber) != surfaceId) {
              return
            }

            session.surfaceIdByPageNumber.delete(pageNumber)
          }),

        releasePageText: discardPageTextInternal
      }

      return service as DocumentRendererService<unknown, PageStructure>
    })
  )
}

/** The application provides the HttpClient that fetches document bytes. */
export const PDFDocumentRendererLive = createPDFDocumentRendererLayer(PDFDocumentRenderer).pipe(
  Layer.provide(Layer.mergeAll(BrowserPageSurfaceLive, BrowserMuPDFPlatformLive))
)
